import { useCallback, useEffect, useRef, useState } from "react";
import { get } from "aws-amplify/api";
import Lottie from "react-lottie-player";
import { NEW_REST_API, NEW_REST_API__V3__STREAMLIT_EMBED_PATH } from "../../consts";
import lottieLoader from "../../assets/new-spinner.json";

// Embeds the ANALYTICS.PROD.PROJECT_ANALYTICS Streamlit app (hosted on Snowflake)
// in an iframe. The backend mints a single-use embed URL scoped to this project;
// we mint a fresh one on every mount (embed URLs are single-use, never cached).
export function DeepAnalytics({ projectId }: { projectId: string }) {
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchEmbedUrl = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { body } = await get({
        apiName: NEW_REST_API,
        path: NEW_REST_API__V3__STREAMLIT_EMBED_PATH(projectId),
      }).response;
      const { embedUrl } = (await body.json()) as unknown as {
        embedUrl: string;
      };
      setEmbedUrl(embedUrl);
    } catch (e: any) {
      console.error("Failed to load deep analytics embed URL:", e);
      setError(e?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchEmbedUrl();
  }, [fetchEmbedUrl]);

  // Snowflake posts lifecycle events from the embedded app. When the session
  // expires we re-mint the URL so the iframe keeps working.
  const embedUrlRef = useRef<string | null>(null);
  embedUrlRef.current = embedUrl;
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!event.origin.endsWith(".snowflake.app")) return;
      switch (event.data?.type) {
        case "SNOWFLAKE_EMBED_SESSION_EXPIRED":
          fetchEmbedUrl();
          break;
        case "SNOWFLAKE_EMBED_ERROR":
          setError("The analytics app failed to load.");
          break;
        default:
          break;
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [fetchEmbedUrl]);

  if (loading && !embedUrl) {
    return (
      <div className="spinner">
        <Lottie
          play
          loop
          speed={1}
          animationData={lottieLoader}
          direction={1}
          style={{ height: 150, width: 150 }}
        />
        <span>Loading analytics…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="spinner">
        <span>Failed to load analytics: {error}</span>
      </div>
    );
  }

  if (!embedUrl) return null;

  return (
    <iframe
      title="Project Deep Analytics"
      src={embedUrl}
      style={{ width: "100%", height: "calc(100vh - 120px)", border: "none" }}
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
    />
  );
}
